import "./App.css";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import ReservationCountdown from "@/global/components/ReservationCountdown/ReservationCountdown";
import AppSnackbarHost from "@/global/components/AppSnackbar/AppSnackbar";
import { refreshReservationAndMenu } from "@/lib/reservationBootstrap";
import CenteredLoader from "@/global/components/Loader/CenteredLoader";
import { parseStoredOrder } from "@/types/preorderV2";
import BrandAccent from "@/v2/components/BrandAccent";
import { isReservationLocked } from "@/v2/hooks/useReservationV2";

const Landing = lazy(() => import("./screens/Landing/Landing"));
const MenuPage = lazy(() => import("./screens/MenuPage/MenuPage"));
const Place = lazy(() => import("./screens/Place/Place"));
const Ticket = lazy(() => import("./screens/Ticket/Ticket"));
const TicketEdit = lazy(() => import("./screens/Ticket/TicketEdit"));
const ReservationLoader = lazy(
  () => import("@/screens/PreOrderHome/ReservationLoader"),
);
const NoReservation = lazy(
  () => import("@/screens/NoReservation/NoReservation"),
);

function App() {
  const reservation = useAppSelector((s) => s.reservation);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const lastFetchedIdRef = useRef<string | undefined>(undefined);
  const [bootstrapping, setBootstrapping] = useState(false);

  // Closed to edits (the clock, or the server's editLock - FOYR-432): every
  // editing route is the read-only ticket.
  const isExpired = isReservationLocked(reservation);
  const hasReservation = Boolean(
    reservation.reservationId && reservation.loaded,
  );
  const hasOrderItems = parseStoredOrder(reservation.order) !== null;
  // Set by "Edit Order": while editing, "/" is the add-items page rather
  // than a redirect back to the ticket.
  const isEditingOrder = useAppSelector((s) => s.ui.isEditingOrder);

  // On app mount/refresh, fetch latest reservation + menu if we have an id
  // and we're not currently on the reservation bootstrap route.
  useEffect(() => {
    const isOnReservationBootstrapRoute =
      location.pathname.startsWith("/reservation/");
    const id = reservation.reservationId;
    if (!id || isOnReservationBootstrapRoute) return;

    (async () => {
      try {
        if (lastFetchedIdRef.current === id) return;
        lastFetchedIdRef.current = id;
        setBootstrapping(true);
        await refreshReservationAndMenu(dispatch, String(id));
        // A soft refresh keeps the guest where they were. Every V2 route
        // settles itself against the fresh data: "/" resolves to the
        // ticket or the landing page, and Place / Ticket / Edit / a menu
        // page redirect when their cart, order or menu is gone.
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to refresh reservation on app load:", e);
      }
      setBootstrapping(false);
    })();
    // Only run on mount and when reservationId changes; ignore route changes to avoid double fetching post-bootstrap
  }, [dispatch, reservation.reservationId, location.pathname]);

  return (
    <>
      <ReservationCountdown />
      <BrandAccent />
      <AppSnackbarHost />
      {bootstrapping ? (
        <CenteredLoader />
      ) : (
        <Suspense fallback={<CenteredLoader />}>
          {hasReservation ? (
            <Routes>
              <Route
                path="/reservation/:reservationId"
                element={<ReservationLoader />}
              />
              <Route
                path="/"
                element={
                  isExpired || (hasOrderItems && !isEditingOrder) ? (
                    <Navigate to="/ticket" replace />
                  ) : (
                    <Landing />
                  )
                }
              />
              <Route
                path="/menu/:menuId"
                element={
                  isExpired ? <Navigate to="/ticket" replace /> : <MenuPage />
                }
              />
              <Route
                path="/place"
                element={
                  isExpired ? <Navigate to="/ticket" replace /> : <Place />
                }
              />
              <Route path="/ticket" element={<Ticket />} />
              <Route
                path="/ticket/edit"
                element={
                  isExpired ? <Navigate to="/ticket" replace /> : <TicketEdit />
                }
              />
              {/* Pre-V2 paths (FOYR-563), kept for links and bookmarks. */}
              <Route path="/menu" element={<Navigate to="/" replace />} />
              <Route path="/menu/drinks" element={<Navigate to="/" replace />} />
              <Route path="/menu/bites" element={<Navigate to="/" replace />} />
              <Route
                path="/menu/item/:itemId"
                element={<Navigate to="/" replace />}
              />
              <Route path="/cart" element={<Navigate to="/place" replace />} />
              <Route
                path="/order-confirmed"
                element={<Navigate to="/ticket" replace />}
              />
              {/*
                /no-reservation and a catch-all both render NoReservation
                here too, matching the `!hasReservation` tree below. Without
                these, a guest routed to /no-reservation while `hasReservation`
                is still (stale-)true — e.g. revisiting a reservation that has
                since been cancelled, where the reservationId + `loaded: true`
                from a prior successful load hadn't been cleared yet — hit a
                <Routes> with no matching Route, which renders nothing: a
                blank page instead of the "no longer available" message.
              */}
              <Route path="/no-reservation" element={<NoReservation />} />
              <Route path="*" element={<NoReservation />} />
            </Routes>
          ) : (
            <Routes>
              <Route
                path="/reservation/:reservationId"
                element={<ReservationLoader />}
              />
              <Route path="*" element={<NoReservation />} />
            </Routes>
          )}
        </Suspense>
      )}
    </>
  );
}

export default App;
